import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import ProfileAvatarImage, { getAvatarCropStyle } from '@/components/ProfileAvatarImage';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  MessageCircle, Clock, Sparkles, ExternalLink,
  Share2, Filter, X, AlertTriangle, Info, ScrollText, Lock, Undo2, Crown, Heart, Shield, ChevronUp
} from 'lucide-react';
import {
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider
} from '@/components/ui/tooltip';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent,
  AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogAction, AlertDialogCancel
} from '@/components/ui/alert-dialog';
import { ShhIcon } from '@/components/icons/ShhIcon';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { triggerVerifySparkle } from '@/components/Layout';
import { TierBadge, getTierRingClass } from '@/components/TierBadge';
import { API_URL } from '@/lib/api';
import { setPageSeo } from '@/lib/seo';
const ALLOWED_TABS = new Set(['main', 'filtered', 'archives', 'about', 'rules', 'contributors']);
const HERO_IMAGE = `${process.env.PUBLIC_URL || ''}/good-quiets-evil.jpg`;
const HERO_IMAGE_WIDTH = 960;
const HERO_IMAGE_HEIGHT = 502;
const TAG_SPLIT_REGEX = /(#(?:S\d+|C\d+|A\d+|Poopy\d+))/g;
const TAG_EXACT_REGEX = /^#(?:S\d+|C\d+|A\d+|Poopy\d+)$/;
const FEED_MEMORY_PREFIX = 'secretsclub-feed-memory:';
const FEED_RESTORE_PREFIX = 'secretsclub-feed-restore:';
const FEED_RESTORE_INTENT_KEY = 'secretsclub-feed-restore-intent';
const FEED_MEMORY_VERSION = 1;
const FEED_RESTORE_TARGET_Y = 120;
const feedMemoryCache = new Map();

const isPostFeedTab = (tab) => tab === 'main' || tab === 'filtered' || tab === 'archives';

const readFeedMemory = (key) => {
  const cached = feedMemoryCache.get(key);
  if (cached?.version === FEED_MEMORY_VERSION && Array.isArray(cached.posts)) {
    return cached;
  }

  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== FEED_MEMORY_VERSION || !Array.isArray(parsed.posts)) return null;
    return parsed;
  } catch (error) {
    return null;
  }
};

const readFeedRestoreMeta = (key) => {
  try {
    const raw = sessionStorage.getItem(key.replace(FEED_MEMORY_PREFIX, FEED_RESTORE_PREFIX));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== FEED_MEMORY_VERSION) return null;
    return parsed;
  } catch (error) {
    return null;
  }
};

const getFeedAnchor = () => {
  const cards = Array.from(document.querySelectorAll('[data-feed-post-id]'));
  if (cards.length === 0) return { anchorPostId: null, anchorOffset: 0 };

  const visibleCard = cards.find((card) => {
    const rect = card.getBoundingClientRect();
    return rect.top <= FEED_RESTORE_TARGET_Y && rect.bottom >= FEED_RESTORE_TARGET_Y;
  }) || cards.find((card) => {
    const rect = card.getBoundingClientRect();
    return rect.bottom > 0;
  });

  if (!visibleCard) return { anchorPostId: null, anchorOffset: 0 };
  const rect = visibleCard.getBoundingClientRect();
  return {
    anchorPostId: visibleCard.getAttribute('data-feed-post-id'),
    anchorOffset: FEED_RESTORE_TARGET_Y - rect.top,
  };
};

const getTabForTag = (tag) => {
  if (typeof tag !== 'string') return 'main';
  if (tag.startsWith('#Poopy')) return 'filtered';
  if (tag.startsWith('#A')) return 'archives';
  return 'main';
};

const getPublicPostContent = (content) =>
  content === 'filtered to poopy butthole'
    ? 'Moved to the Poopy Butthole section by moderation.'
    : content;

const getPremiumLockStyles = (roleName) => {
  switch (roleName) {
    case 'Quiet Mode':
      return {
        cardClass: 'border-blue-400/70 dark:border-blue-500/70 bg-card/95 backdrop-blur-sm',
        iconClass: 'text-blue-500 dark:text-blue-400',
        badgeClass: 'bg-blue-600 text-white',
        linkClass: 'text-blue-600 dark:text-blue-400',
      };
    case 'Needlessly Rich':
      return {
        cardClass: 'border-amber-400/70 dark:border-amber-500/70 bg-card/95 backdrop-blur-sm',
        iconClass: 'text-amber-500 dark:text-amber-400',
        badgeClass: 'bg-amber-500 text-amber-950',
        linkClass: 'text-amber-600 dark:text-amber-400',
      };
    case 'After Hours':
    default:
      return {
        cardClass: 'border-purple-400/70 dark:border-purple-500/70 bg-card/95 backdrop-blur-sm',
        iconClass: 'text-purple-500 dark:text-purple-400',
        badgeClass: 'bg-purple-600 text-white',
        linkClass: 'text-purple-600 dark:text-purple-400',
      };
  }
};

const getArchiveUpsellStyles = (roleName) => {
  switch (roleName) {
    case 'Quiet Mode':
      return {
        cardClass: 'border-blue-500/50 bg-blue-50/50 dark:bg-blue-900/10',
        iconClass: 'text-blue-600 dark:text-blue-400',
        titleClass: 'text-blue-800 dark:text-blue-300',
        textClass: 'text-blue-700 dark:text-blue-400/90',
        buttonClass: 'bg-blue-600 hover:bg-blue-700 text-white',
      };
    case 'Needlessly Rich':
      return {
        cardClass: 'border-amber-500/50 bg-amber-50/50 dark:bg-amber-900/10',
        iconClass: 'text-amber-600 dark:text-amber-400',
        titleClass: 'text-amber-800 dark:text-amber-300',
        textClass: 'text-amber-700 dark:text-amber-400/90',
        buttonClass: 'bg-gradient-to-r from-rose-500 to-amber-400 hover:opacity-90 text-white',
      };
    case 'After Hours':
      return {
        cardClass: 'border-violet-500/50 bg-violet-50/50 dark:bg-violet-900/10',
        iconClass: 'text-violet-600 dark:text-violet-400',
        titleClass: 'text-violet-800 dark:text-violet-300',
        textClass: 'text-violet-700 dark:text-violet-400/90',
        buttonClass: 'bg-gradient-to-r from-violet-600 to-indigo-900 hover:opacity-90 text-white',
      };
    default:
      return {
        cardClass: 'border-sky-500/50 bg-sky-50/50 dark:bg-sky-900/10',
        iconClass: 'text-sky-600 dark:text-sky-400',
        titleClass: 'text-sky-800 dark:text-sky-300',
        textClass: 'text-sky-700 dark:text-sky-400/90',
        buttonClass: 'btn-primary',
      };
  }
};

// Share Button Component
const ShareButtons = ({ post }) => {
  const postUrl = `${window.location.origin}/post/${post.post_id}`;
  const shareText = `Check out this confession: "${post.tag}" - See what people say about this!`;
  
  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(postUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };
  
  const shareToTwitter = () => {
    const url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(postUrl)}&text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };
  
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={shareToFacebook}
        className="p-2 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600"
        title="Share on Facebook"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      </button>
      <button
        onClick={shareToTwitter}
        className="p-2 rounded-lg hover:bg-sky-100 dark:hover:bg-sky-900/30 text-sky-500"
        title="Share on X (Twitter)"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      </button>
    </div>
  );
};

// Tag Component - clickable
const PostTag = ({ tag, onClick, isFilter = false }) => (
  <button
    onClick={() => onClick(tag)}
    className={`font-mono text-xs px-2 py-1 rounded ${
      isFilter
        ? 'bg-forest-600 text-white'
        : 'bg-forest-100 dark:bg-forest-900/30 text-forest-700 dark:text-forest-400 hover:bg-forest-200 dark:hover:bg-forest-900/50'
    }`}
  >
    {tag}
  </button>
);

// Render content with clickable tags
const RichContent = ({ text, onTagClick, className = '' }) => {
  if (!text) return null;
  const parts = text.split(TAG_SPLIT_REGEX);
  return (
    <p className={className}>
      {parts.map((part, i) =>
        TAG_EXACT_REGEX.test(part) ? (
          <button
            key={i}
            onClick={() => onTagClick(part)}
            className="font-mono text-forest-700 dark:text-forest-400 hover:underline cursor-pointer"
            data-testid={`inline-tag-${part}`}
          >
            {part}
          </button>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </p>
  );
};

// Undo Move Button with countdown
const UndoButton = ({ secondsLeft, onClick, postId }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/60 transition-colors"
    data-testid={`undo-move-btn-${postId}`}
  >
    <Undo2 className="w-3.5 h-3.5" />
    Undo ({secondsLeft}s)
  </button>
);

// Post Card Component
const PostCard = ({ post, index, onTopicClick, onTagClick, user, roleOrder, onMoveToPoopyButthole, undoInfo, onUndoMove, onImageClick, onReact, allowedEmojis, feedLocation, onToggleAdEligibility, onBeforeOpenPost, onBackToTop }) => {
  const formattedDate = post.created_at 
    ? formatDistanceToNow(new Date(post.created_at), { addSuffix: true })
    : 'Recently';

  // Extract mentioned tags from content (excluding the main tag)
  const mentionedTags = post.mentioned_tags || [];

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const canMove = isAdmin && !post.is_filtered && !post.is_archived && post.content !== 'filtered to poopy butthole';
  const hasUndo = !!undoInfo;
  const isViewerUnverified = user && !user.email_verified && !user.oauth_provider;

  // Check if user can view premium post
  const isPremium = !!post.premium_role;
  const premiumLocked = post.locked_content && post.locked_reason === 'premium';
  const premiumLabel = post.is_filtered ? 'Poopy Butthole Premium Post' : (post.tag || 'Premium Post');
  const membershipPath = post.locked_cta_url || `/membership/${post.premium_role === 'After Hours' ? 'after-hours' : post.premium_role === 'Needlessly Rich' ? 'needlessly-rich' : 'quiet-mode'}`;
  const premiumLockStyles = getPremiumLockStyles(post.premium_role);
  const canViewPremium = (() => {
    if (!isPremium) return true;
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'superadmin') return true;
    // Check by premium tier level OR by having after_hours_access permission
    const userTier = user.premium_tier || 0;
    const requiredTier = roleOrder[post.premium_role] ?? 0;
    if (userTier >= requiredTier) return true;
    if (user.premium_permissions?.includes('after_hours_access') && requiredTier <= 2) return true;
    return false;
  })();

  const handleReact = (emoji) => {
    if (!user) return;
    if (isViewerUnverified) {
      triggerVerifySparkle();
      return;
    }
    onReact(post.post_id, emoji);
  };

  const postLinkState = feedLocation ? { fromFeed: feedLocation } : undefined;

  if (premiumLocked || (isPremium && !canViewPremium)) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: index * 0.05 }}
      >
        <Card className={`overflow-hidden ${premiumLockStyles.cardClass}`} data-testid={`premium-locked-${post.post_id}`}>
          <CardContent className="py-8 text-center">
            <Lock className={`w-10 h-10 mx-auto mb-3 ${premiumLockStyles.iconClass}`} />
            <p className="font-serif text-lg font-semibold mb-1">{premiumLabel}</p>
            <p className="text-sm text-muted-foreground mb-3">
              This post is exclusive to <span className={`inline-flex items-center rounded-full text-xs font-medium px-2 py-0.5 ml-1 ${premiumLockStyles.badgeClass}`}>{post.premium_role}</span> members and above.
            </p>
            {user ? (
              <Link to={membershipPath} className={`inline-flex items-center gap-1.5 text-sm font-medium hover:underline ${premiumLockStyles.linkClass}`} data-testid={`upgrade-cta-${post.post_id}`}>
                <Crown className="w-4 h-4" /> Upgrade to unlock
              </Link>
            ) : (
              <Link to="/register" className={`inline-flex items-center gap-1.5 text-sm font-medium hover:underline ${premiumLockStyles.linkClass}`}>
                Join to unlock premium content
              </Link>
            )}
            {post.topic_name && (
              <Badge variant="outline" className="mt-2 block mx-auto w-fit">{post.topic_name}</Badge>
            )}
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  // Adblock-locked placeholder: server returned a gated post
  if (post.locked_content) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: index * 0.05 }}
      >
        <Card className="overflow-hidden border-amber-200 dark:border-amber-800/60 bg-gradient-to-br from-amber-50/50 to-background dark:from-amber-950/10" data-testid={`adblock-locked-${post.post_id}`}>
          <CardContent className="py-10 text-center">
            <Lock className="w-12 h-12 mx-auto text-amber-500 dark:text-amber-400 mb-4" />
            <h3 className="font-serif text-xl font-semibold mb-3">Ad Blocker Detected</h3>
            <p className="text-muted-foreground leading-relaxed max-w-md mx-auto mb-4">
              It appears that you're using an ad blocker. Secrets Club is free because of the ads we show.
            </p>
            <p className="text-muted-foreground leading-relaxed max-w-md mx-auto mb-6">
              If you'd like to see the real secrets, you can either turn off your ad blocker and refresh the page, or become a member.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/membership" data-testid="adblock-cta-membership">
                <Button className="btn-primary px-6">
                  <Crown className="w-4 h-4 mr-2" />
                  Become a Quiet Mode Member
                </Button>
              </Link>
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
                data-testid="adblock-cta-refresh"
              >
                I've Disabled My Ad Blocker
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      data-feed-post-id={post.post_id}
    >
      <Card className="card-hover group" data-testid={`post-card-${post.post_id}`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Avatar className={`w-6 h-6 ${getTierRingClass(post.author_premium_tier)}`}>
                {post.author_profile_picture && (
                  <ProfileAvatarImage
                    src={post.author_profile_picture}
                    alt={post.author_username}
                    zoom={post.author_profile_picture_zoom}
                    positionX={post.author_profile_picture_position_x}
                    positionY={post.author_profile_picture_position_y}
                  />
                )}
                <AvatarFallback className="bg-forest-100 text-forest-700 text-xs">{post.author_username?.[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              <span className="font-medium text-foreground">{post.author_username}</span>
              {post.author_premium_tier > 0 && (
                <TierBadge tier={post.author_premium_tier} roleName={post.author_premium_role_name} />
              )}
              <span>·</span>
              <Clock className="w-3 h-3" />
              <span>{formattedDate}</span>
            </div>
            <div className="flex items-center gap-1">
              {hasUndo && (
                <UndoButton
                  secondsLeft={undoInfo.secondsLeft}
                  onClick={() => onUndoMove(post.post_id)}
                  postId={post.post_id}
                />
              )}
              {canMove && !hasUndo && (
                <AlertDialog>
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <AlertDialogTrigger asChild>
                          <button
                            className="p-2 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 text-lg leading-none transition-colors"
                            data-testid={`move-to-pb-btn-${post.post_id}`}
                          >
                            💩
                          </button>
                        </AlertDialogTrigger>
                      </TooltipTrigger>
                      <TooltipContent>Move to Poopy Butthole</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <AlertDialogContent data-testid="move-to-pb-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Move to Poopy Butthole?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will move the post content to the Poopy Butthole section and replace the original text with a moderation placeholder. The post's tag and position in the feed will be preserved.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="move-to-pb-cancel">Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => onMoveToPoopyButthole(post.post_id)}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid="move-to-pb-confirm"
                      >
                        Move It
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              {isAdmin && (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => onToggleAdEligibility(post)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                          post.ad_eligible === false
                            ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300'
                            : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300'
                        }`}
                        data-testid={`ad-eligibility-toggle-${post.post_id}`}
                      >
                        {post.ad_eligible === false ? 'No Ads' : 'Ads OK'}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {post.ad_eligible === false ? 'Allow ads on this post' : 'Mark this post ad ineligible'}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <ShareButtons post={post} />
            </div>
          </div>
          
          {/* Tag and Topic */}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {post.is_archived && (
              <Badge variant="outline" className="border-sky-400/50 text-sky-700 dark:text-sky-300">
                Archives
              </Badge>
            )}
            {post.topic_name && (
              <Badge 
                variant="outline" 
                className="cursor-pointer hover:bg-muted"
                onClick={() => onTopicClick(post.topic_id)}
              >
                {post.topic_name}
              </Badge>
            )}
          </div>
          
          <Link to={`/post/${post.post_id}`} state={postLinkState} onClick={() => onBeforeOpenPost?.()}>
            <h2 className="font-mono text-xl md:text-2xl font-semibold group-hover:text-forest-700 dark:group-hover:text-forest-400 transition-colors">
              {post.tag}
            </h2>
          </Link>
        </CardHeader>
        <CardContent>
          {/* Image - below tag, above text, indented and smaller */}
          {post.image_url && (
            <div className="mx-4 sm:mx-8 mb-4">
              <img 
                src={post.image_url} 
                alt={post.title}
                className="max-w-full max-h-80 rounded-lg object-contain cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => onImageClick(post.image_url)}
                data-testid={`post-image-${post.post_id}`}
              />
            </div>
          )}

          <RichContent 
            text={getPublicPostContent(post.content)} 
            onTagClick={onTagClick} 
            className="text-muted-foreground leading-relaxed whitespace-pre-wrap"
          />
          
          {/* Mentioned tags */}
          {mentionedTags.length > 0 && (
            <div className="flex flex-wrap items-center gap-1 mt-3">
              <span className="text-xs text-muted-foreground">References:</span>
              {mentionedTags.map(tag => (
                <PostTag key={tag} tag={tag} onClick={onTagClick} />
              ))}
            </div>
          )}

          {/* Post Reactions */}
          {(Object.keys(post.reactions || {}).length > 0 || user) && (
            <div className="flex flex-wrap items-center gap-1.5 mt-3">
              {Object.entries(post.reactions || {}).map(([emoji, users]) => (
                users.length > 0 && (
                  <button
                    key={emoji}
                    onClick={() => handleReact(emoji)}
                    className={`flex items-center gap-1 text-sm rounded-full px-2.5 py-1 transition-colors ${
                      user && users.includes(user.user_id)
                        ? 'bg-forest-100 dark:bg-forest-900/40 border border-forest-300 dark:border-forest-700'
                        : 'bg-muted/50 hover:bg-muted'
                    }`}
                    data-testid={`post-reaction-${emoji}-${post.post_id}`}
                  >
                    <span>{emoji}</span>
                    <span className="text-xs text-muted-foreground">{users.length}</span>
                  </button>
                )
              ))}
              {user && (
                <PostReactionPicker
                  allowedEmojis={allowedEmojis}
                  onReact={handleReact}
                />
              )}
            </div>
          )}
          
          <div className="flex items-center justify-between gap-4 mt-4 pt-4 border-t border-border">
            <Link 
              to={`/post/${post.post_id}`}
              state={postLinkState}
              onClick={() => onBeforeOpenPost?.()}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              <MessageCircle className="w-4 h-4" />
              <span>{post.comment_count} comments</span>
            </Link>
            <button
              type="button"
              onClick={onBackToTop}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              data-testid={`post-back-to-top-${post.post_id}`}
            >
              <ChevronUp className="w-3.5 h-3.5" />
              Back to top
            </button>
          </div>

          {/* Latest Comment Preview */}
          {post.latest_comment && (
            <Link
              to={`/post/${post.post_id}`}
              state={postLinkState}
              onClick={() => onBeforeOpenPost?.()}
              className="block mt-3 p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
              data-testid={`latest-comment-${post.post_id}`}
            >
              <p className="text-xs text-muted-foreground mb-1">
                Latest comment by <span className="font-medium text-foreground">{post.latest_comment.display_name}</span>
              </p>
              <p className="text-sm text-muted-foreground line-clamp-2">{post.latest_comment.content}</p>
            </Link>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Post Reaction Picker (mini emoji picker for posts)
const PostReactionPicker = ({ allowedEmojis, onReact }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="p-1 px-2 rounded-full bg-muted/50 hover:bg-muted text-sm text-muted-foreground" data-testid="post-reaction-add">+</button>
      {open && (
        <div className="absolute bottom-full left-0 mb-2 p-2.5 bg-card border rounded-xl shadow-lg z-50 flex flex-wrap gap-1.5" style={{ width: 'max-content', maxWidth: '360px' }}>
          {(allowedEmojis || ["👍", "❤️", "😂", "😮", "😢", "😡"]).map(emoji => (
            <button key={emoji} onClick={() => { onReact(emoji); setOpen(false); }} className="text-xl px-2 py-1.5 rounded hover:bg-muted whitespace-nowrap">{emoji}</button>
          ))}
        </div>
      )}
    </div>
  );
};

// Ad Placeholder Component with admin-configurable frequencies
// Currently ads are not populated (no AdSense). This component renders nothing
// until real ad content is available. When AdSense is integrated, the placeholder
// will be replaced with actual ad code.
const AdPlaceholder = ({ user, postIndex, adFrequencyGuest, adFrequencyMember }) => {
  // Users with 'no_ads' permission never see ads
  if (user?.premium_permissions?.includes('no_ads')) return null;
  // No real ads are configured yet - render nothing
  // When Google AdSense is integrated, this will check for ad content
  // and only render if the ad slot is actually populated
  return null;
};

// Topic Filter Bar
const TopicFilterBar = ({ topics, selectedTopic, onTopicSelect, selectedTag, onClearTag }) => {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-6 p-4 bg-card rounded-xl border border-border">
      <Filter className="w-4 h-4 text-muted-foreground" />
      <button
        onClick={() => onTopicSelect(null)}
        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
          !selectedTopic && !selectedTag
            ? 'bg-forest-800 text-white'
            : 'bg-muted hover:bg-muted/80'
        }`}
      >
        All
      </button>
      {topics.map(topic => (
        <button
          key={topic.topic_id}
          onClick={() => onTopicSelect(topic.topic_id)}
          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
            selectedTopic === topic.topic_id
              ? 'bg-forest-800 text-white'
              : 'bg-muted hover:bg-muted/80'
          }`}
        >
          {topic.name}
          {topic.post_count > 0 && (
            <span className="ml-1 text-xs opacity-70">({topic.post_count})</span>
          )}
        </button>
      ))}
      
      {selectedTag && (
        <div className="flex items-center gap-1 ml-2">
          <span className="text-xs text-muted-foreground">Showing:</span>
          <Badge className="bg-forest-600 text-white">
            {selectedTag}
            <button onClick={onClearTag} className="ml-1">
              <X className="w-3 h-3" />
            </button>
          </Badge>
        </div>
      )}
    </div>
  );
};

// Skeleton Loader
const PostSkeleton = () => (
  <Card className="overflow-hidden">
    <Skeleton className="aspect-video w-full" />
    <CardHeader>
      <div className="flex items-center gap-2 mb-2">
        <Skeleton className="w-6 h-6 rounded-full" />
        <Skeleton className="w-24 h-4" />
        <Skeleton className="w-16 h-4" />
      </div>
      <Skeleton className="h-7 w-3/4" />
    </CardHeader>
    <CardContent>
      <Skeleton className="h-4 w-full mb-2" />
      <Skeleton className="h-4 w-5/6" />
    </CardContent>
  </Card>
);

// Main Description Component
const SiteDescription = () => (
  <div className="text-center max-w-3xl mx-auto mb-4">
    <p className="text-muted-foreground leading-relaxed">
      This is an anonymous confessions and/or advice site. You can also just vent anonymously. 
      We offer TRUE anonymity, even the admins don't know who posted a confession.
    </p>
    <p className="text-muted-foreground leading-relaxed mt-2">
      Get things off your mind, get advice, confess your sins, express your spiciest opinions, 
      all from the safety of true and complete anonymity.
    </p>
    <p className="text-muted-foreground leading-relaxed mt-2 italic">
      You'll never find a more wretched hive of scum and villainy anywhere else on the internet!
    </p>
  </div>
);

export default function HomePage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const initialTab = searchParams.get('tab');
  
  const [posts, setPosts] = useState([]);
  const [topics, setTopics] = useState([]);
  const [googleFormUrl, setGoogleFormUrl] = useState('');
  const [submissionMode, setSubmissionMode] = useState('google_form');
  const [aboutText, setAboutText] = useState('');
  const [rules, setRules] = useState([]);
  const [contributors, setContributors] = useState({ tiers: [], message: '' });
  const [contributorsLoading, setContributorsLoading] = useState(false);
  const [contributorsLoaded, setContributorsLoaded] = useState(false);
  const [adFrequencyGuest, setAdFrequencyGuest] = useState(1);
  const [adFrequencyMember, setAdFrequencyMember] = useState(3);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [roleOrder, setRoleOrder] = useState({});
  const [archiveAccessTiers, setArchiveAccessTiers] = useState([]);
  const [undoMap, setUndoMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);
  const feedMemoryKey = `${FEED_MEMORY_PREFIX}${location.pathname}${location.search}`;
  const feedHref = `${location.pathname}${location.search}`;

  useEffect(() => {
    setPageSeo({
      title: 'Secrets Club - Anonymous Confessions & Advice',
      description: 'Read anonymous confessions, ask for advice, and join moderated discussion on Secrets Club.',
      path: '/',
    });
  }, []);
  const [allowedEmojis, setAllowedEmojis] = useState(["👍", "❤️", "😂", "😮", "😢", "😡"]);
  const canViewArchives = !!user && (user.role === 'admin' || user.role === 'superadmin' || user.premium_permissions?.includes('view_archives'));
  const useNativeConfessionFlow = submissionMode === 'native_encrypted' || submissionMode === 'both';
  
  // Filter state
  const [activeTab, setActiveTab] = useState(ALLOWED_TABS.has(initialTab) ? initialTab : 'main');
  const [selectedTopic, setSelectedTopic] = useState(searchParams.get('topic') || null);
  const [selectedTag, setSelectedTag] = useState(searchParams.get('tag') || null);
  
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const pendingFeedRestoreRef = useRef(null);
  const restoredFeedKeyRef = useRef(null);
  const isRestoringFeedRef = useRef(false);
  const scrollStorageKey = `feed-scroll:${location.pathname}${location.search}`;

  const archiveUpsellText = (() => {
    if (archiveAccessTiers.length === 0) {
      return 'Upgrade your membership or receive a role with archive access to unlock this section.';
    }
    if (archiveAccessTiers.length === 1) {
      return `Upgrade to ${archiveAccessTiers[0]} membership to view the Archives.`;
    }
    const names = archiveAccessTiers.join(' or ');
    return `Upgrade to ${names} membership to view the Archives.`;
  })();
  const archiveUpsellRole = archiveAccessTiers[0] || null;
  const archiveUpsellStyles = getArchiveUpsellStyles(archiveUpsellRole);

  const fetchTopics = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/topics`);
      setTopics((response.data.topics || []).sort((a, b) => (b.post_count || 0) - (a.post_count || 0)));
    } catch (err) {
      console.error('Failed to fetch topics:', err);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/public`);
      setGoogleFormUrl(response.data.google_form_url);
      setSubmissionMode(response.data.submission_mode || 'google_form');
      setAboutText(response.data.about_text || '');
      setRules(response.data.rules || []);
      setAdFrequencyGuest(response.data.ad_frequency_guest ?? 1);
      setAdFrequencyMember(response.data.ad_frequency_member ?? 3);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  }, []);

  const fetchContributors = useCallback(async () => {
    setContributorsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/contributors`);
      setContributors({
        tiers: Array.isArray(res.data?.tiers) ? res.data.tiers : [],
        message: res.data?.message || '',
      });
    } catch (err) {
      console.error('Failed to fetch contributors:', err);
    } finally {
      setContributorsLoaded(true);
      setContributorsLoading(false);
    }
  }, []);

  const fetchPosts = useCallback(async (skip = 0, limit = 10) => {
    const params = {
      skip,
      limit,
      is_filtered: activeTab === 'filtered',
      is_archived: activeTab === 'archives',
    };
    if (activeTab === 'main' && selectedTopic) params.topic_id = selectedTopic;
    if (selectedTag) params.tag = selectedTag;
    
    const response = await axios.get(`${API_URL}/api/posts`, { params });
    return response.data;
  }, [activeTab, selectedTopic, selectedTag]);

  const fetchPostsToCount = useCallback(async (targetCount) => {
    const allPosts = [];
    let hasMorePosts = false;
    const safeTarget = Math.max(10, Math.min(targetCount || 10, 250));

    while (allPosts.length < safeTarget) {
      const batchSize = Math.min(50, safeTarget - allPosts.length);
      const data = await fetchPosts(allPosts.length, batchSize);
      const batchPosts = Array.isArray(data?.posts) ? data.posts : [];
      allPosts.push(...batchPosts);
      hasMorePosts = Boolean(data?.has_more);
      if (!hasMorePosts || batchPosts.length === 0) break;
    }

    return { posts: allPosts, hasMore: hasMorePosts };
  }, [fetchPosts]);

  const saveFeedMemory = useCallback((persistToSession = true) => {
    if (!isPostFeedTab(activeTab) || posts.length === 0) return;

    const anchor = getFeedAnchor();
    const snapshot = {
      version: FEED_MEMORY_VERSION,
      key: feedMemoryKey,
      tab: activeTab,
      search: location.search,
      selectedTopic,
      selectedTag,
      posts,
      hasMore,
      scrollY: window.scrollY,
      anchorPostId: anchor.anchorPostId,
      anchorOffset: anchor.anchorOffset,
      savedAt: Date.now(),
    };
    const restoreMeta = {
      version: FEED_MEMORY_VERSION,
      key: feedMemoryKey,
      tab: activeTab,
      search: location.search,
      selectedTopic,
      selectedTag,
      loadedCount: posts.length,
      hasMore,
      scrollY: window.scrollY,
      anchorPostId: anchor.anchorPostId,
      anchorOffset: anchor.anchorOffset,
      savedAt: Date.now(),
    };

    feedMemoryCache.set(feedMemoryKey, snapshot);

    try {
      sessionStorage.setItem(feedMemoryKey.replace(FEED_MEMORY_PREFIX, FEED_RESTORE_PREFIX), JSON.stringify(restoreMeta));
    } catch (error) {
      // Compact restore metadata should fit; if it does not, the in-memory cache remains available.
    }

    if (persistToSession) {
      try {
        sessionStorage.setItem(feedMemoryKey, JSON.stringify(snapshot));
      } catch (error) {
        sessionStorage.removeItem(feedMemoryKey);
      }
    }
    sessionStorage.setItem(scrollStorageKey, String(window.scrollY));
  }, [activeTab, feedMemoryKey, hasMore, location.search, posts, scrollStorageKey, selectedTag, selectedTopic]);

  // Initial load
  useEffect(() => {
    const loadInitialData = async () => {
      const restoreIntent = sessionStorage.getItem(FEED_RESTORE_INTENT_KEY);
      const hasRestoreIntent = restoreIntent === feedHref;
      const savedFeed = readFeedMemory(feedMemoryKey);
      const savedRestoreMeta = savedFeed ? null : readFeedRestoreMeta(feedMemoryKey);
      const canUseSavedFeed = isPostFeedTab(activeTab)
        && !(activeTab === 'archives' && !canViewArchives)
        && savedFeed
        && savedFeed.tab === activeTab
        && savedFeed.search === location.search
        && savedFeed.selectedTopic === selectedTopic
        && savedFeed.selectedTag === selectedTag
        && savedFeed.posts.length > 0;
      const canUseRestoreMeta = isPostFeedTab(activeTab)
        && !(activeTab === 'archives' && !canViewArchives)
        && savedRestoreMeta
        && savedRestoreMeta.tab === activeTab
        && savedRestoreMeta.search === location.search
        && savedRestoreMeta.selectedTopic === selectedTopic
        && savedRestoreMeta.selectedTag === selectedTag
        && savedRestoreMeta.loadedCount > 0;

      setLoading(!canUseSavedFeed);
      if (canUseSavedFeed) {
        pendingFeedRestoreRef.current = savedFeed;
        restoredFeedKeyRef.current = null;
        setPosts(savedFeed.posts);
        setHasMore(Boolean(savedFeed.hasMore));
      } else {
        pendingFeedRestoreRef.current = canUseRestoreMeta ? savedRestoreMeta : null;
        setPosts([]);
      }

      try {
        // Fetch role order for premium posts
        axios.get(`${API_URL}/api/roles`).then(res => {
          const order = {};
          const archiveRoles = [];
          (res.data.roles || []).forEach(r => {
            order[r.name] = r.tier_level ?? r.order ?? 0;
            if (r.is_premium && Array.isArray(r.permissions) && r.permissions.includes('view_archives')) {
              archiveRoles.push(r);
            }
          });
          archiveRoles.sort((a, b) => (a.tier_level ?? a.order ?? 0) - (b.tier_level ?? b.order ?? 0));
          setRoleOrder(order);
          setArchiveAccessTiers(archiveRoles.map(r => r.name));
        }).catch(() => {});

        await Promise.all([fetchTopics(), fetchSettings()]);
        if (activeTab === 'archives' && !canViewArchives) {
          setPosts([]);
          setHasMore(false);
        } else if (!canUseSavedFeed && isPostFeedTab(activeTab)) {
          if (canUseRestoreMeta) {
            const restored = await fetchPostsToCount(savedRestoreMeta.loadedCount);
            setPosts(restored.posts);
            setHasMore(Boolean(restored.hasMore));
          } else {
            const data = await fetchPosts(0);
            setPosts(Array.isArray(data?.posts) ? data.posts : []);
            setHasMore(Boolean(data?.has_more));
          }
        }
        // Fetch allowed emojis for the user's role
        if (user) {
          axios.get(`${API_URL}/api/auth/me/allowed-emojis`).then(res => {
            if (res.data.allowed_emojis?.length) setAllowedEmojis(res.data.allowed_emojis);
          }).catch(() => {});
        }
      } catch (err) {
        setError('Failed to load posts. Please try again.');
        console.error(err);
      } finally {
        if (hasRestoreIntent) {
          sessionStorage.removeItem(FEED_RESTORE_INTENT_KEY);
        }
        setLoading(false);
      }
    };
    loadInitialData();
  }, [fetchPosts, fetchPostsToCount, fetchTopics, fetchSettings, activeTab, selectedTopic, selectedTag, user, canViewArchives, feedMemoryKey, feedHref, location.search]);

  // Lazy-load contributors when tab is selected
  useEffect(() => {
    if (activeTab === 'contributors' && !contributorsLoaded && !contributorsLoading) {
      fetchContributors();
    }
  }, [activeTab, contributorsLoaded, contributorsLoading, fetchContributors]);

  useEffect(() => {
    const requestedTab = searchParams.get('tab');
    if (requestedTab && ALLOWED_TABS.has(requestedTab) && requestedTab !== activeTab) {
      setActiveTab(requestedTab);
    }
    const requestedTopic = searchParams.get('topic') || null;
    if (requestedTopic !== selectedTopic) {
      setSelectedTopic(requestedTopic);
    }
    const requestedTag = searchParams.get('tag') || null;
    if (requestedTag !== selectedTag) {
      setSelectedTag(requestedTag);
    }
  }, [searchParams, activeTab, selectedTag, selectedTopic]);

  const handleTabChange = (tab) => {
    saveFeedMemory();
    setActiveTab(tab);
    setSelectedTopic(null);
    const nextParams = new URLSearchParams(searchParams);
    if (tab === 'main') nextParams.delete('tab');
    else nextParams.set('tab', tab);
    nextParams.delete('topic');
    setSearchParams(nextParams);
  };

  const buildSearchParams = ({ tab = activeTab, topic = selectedTopic, tag = selectedTag } = {}) => {
    const nextParams = new URLSearchParams();
    if (tab && tab !== 'main') nextParams.set('tab', tab);
    if (topic) nextParams.set('topic', topic);
    if (tag) nextParams.set('tag', tag);
    return nextParams;
  };

  useEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    return () => {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = previousScrollRestoration;
      }
    };
  }, []);

  useEffect(() => {
    const saveScrollPosition = () => {
      if (isRestoringFeedRef.current || !isPostFeedTab(activeTab)) return;
      saveFeedMemory(false);
      sessionStorage.setItem(scrollStorageKey, String(window.scrollY));
    };

    window.addEventListener('scroll', saveScrollPosition, { passive: true });
    return () => {
      saveScrollPosition();
      window.removeEventListener('scroll', saveScrollPosition);
    };
  }, [activeTab, saveFeedMemory, scrollStorageKey]);

  useEffect(() => {
    if (!loading && isPostFeedTab(activeTab) && posts.length > 0) {
      saveFeedMemory(false);
    }
  }, [activeTab, loading, posts.length, saveFeedMemory]);

  useEffect(() => {
    const saveOnPageHide = () => saveFeedMemory();
    window.addEventListener('pagehide', saveOnPageHide);
    return () => {
      saveOnPageHide();
      window.removeEventListener('pagehide', saveOnPageHide);
    };
  }, [saveFeedMemory]);

  useLayoutEffect(() => {
    const snapshot = pendingFeedRestoreRef.current;
    if (loading || !snapshot || restoredFeedKeyRef.current === feedMemoryKey) return;
    if (posts.length === 0) return;

    isRestoringFeedRef.current = true;
    restoredFeedKeyRef.current = feedMemoryKey;

    const restore = () => {
      const anchorEl = snapshot.anchorPostId
        ? document.querySelector(`[data-feed-post-id="${snapshot.anchorPostId}"]`)
        : null;

      if (anchorEl) {
        const rect = anchorEl.getBoundingClientRect();
        const desiredTop = FEED_RESTORE_TARGET_Y - (snapshot.anchorOffset || 0);
        window.scrollBy(0, rect.top - desiredTop);
      } else {
        window.scrollTo(0, snapshot.scrollY || 0);
      }

      window.requestAnimationFrame(() => {
        isRestoringFeedRef.current = false;
        pendingFeedRestoreRef.current = null;
      });
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(restore);
    });
  }, [feedMemoryKey, loading, posts.length]);

  // Infinite scroll
  useEffect(() => {
    if (loading || loadingMore || !hasMore) return;

    const observer = new IntersectionObserver(
      async (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          setLoadingMore(true);
          try {
            const data = await fetchPosts(posts.length);
            const nextPosts = Array.isArray(data?.posts) ? data.posts : [];
            setPosts(prev => [...prev, ...nextPosts]);
            setHasMore(Boolean(data?.has_more));
          } catch (err) {
            console.error('Error loading more posts:', err);
          } finally {
            setLoadingMore(false);
          }
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [posts.length, hasMore, loading, loadingMore, fetchPosts]);

  const handleTopicSelect = (topicId) => {
    saveFeedMemory();
    sessionStorage.setItem(scrollStorageKey, '0');
    setSelectedTopic(topicId);
    setSelectedTag(null);
    setSearchParams(buildSearchParams({ topic: topicId, tag: null }));
  };

  const handleTagClick = (tag) => {
    saveFeedMemory();
    sessionStorage.setItem(scrollStorageKey, '0');
    const nextTab = getTabForTag(tag);
    setActiveTab(nextTab);
    setSelectedTag(tag);
    setSelectedTopic(null);
    setSearchParams(buildSearchParams({ tab: nextTab, topic: null, tag }));
  };

  const handleClearTag = () => {
    saveFeedMemory();
    sessionStorage.setItem(scrollStorageKey, '0');
    setSelectedTag(null);
    setSearchParams(buildSearchParams({ tag: null }));
  };

  const handleImageClick = (imageUrl) => {
    setLightboxImage(imageUrl);
  };

  const handleBackToTop = () => {
    feedMemoryCache.delete(feedMemoryKey);
    sessionStorage.removeItem(feedMemoryKey);
    sessionStorage.removeItem(feedMemoryKey.replace(FEED_MEMORY_PREFIX, FEED_RESTORE_PREFIX));
    sessionStorage.setItem(scrollStorageKey, '0');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePostReaction = async (postId, emoji) => {
    try {
      const res = await axios.post(`${API_URL}/api/posts/${postId}/reactions`, { emoji });
      setPosts(prev => prev.map(p => p.post_id === postId ? { ...p, reactions: res.data.reactions } : p));
    } catch (error) {
      console.error('Failed to react to post:', error);
    }
  };

  const handleToggleAdEligibility = async (post) => {
    const nextAdEligible = post.ad_eligible === false;
    try {
      const res = await axios.patch(`${API_URL}/api/posts/${post.post_id}/ad-eligibility`, {
        ad_eligible: nextAdEligible,
      });
      setPosts(prev => prev.map(p => (
        p.post_id === post.post_id
          ? {
              ...p,
              ad_eligible: res.data.ad_eligible,
              ad_ineligible: res.data.ad_ineligible,
              ad_ineligible_reason: res.data.ad_ineligible_reason,
            }
          : p
      )));
    } catch (error) {
      console.error('Failed to update ad eligibility:', error);
      alert(error.response?.data?.detail || 'Failed to update ad eligibility');
    }
  };

  const handleMoveToPoopyButthole = async (postId) => {
    try {
      const res = await axios.post(`${API_URL}/api/posts/${postId}/move-to-poopy-butthole`);
      const newPostId = res.data.new_post_id;

      // Save original content before overwriting local state
      const originalContent = posts.find(p => p.post_id === postId)?.content;

      setPosts(prev =>
        prev.map(p =>
          p.post_id === postId
            ? { ...p, content: 'filtered to poopy butthole' }
            : p
        )
      );

      // Start 10-second countdown
      let seconds = 10;
      setUndoMap(prev => ({ ...prev, [postId]: { newPostId, originalContent, secondsLeft: seconds } }));

      const interval = setInterval(() => {
        seconds -= 1;
        if (seconds <= 0) {
          clearInterval(interval);
          setUndoMap(prev => { const next = { ...prev }; delete next[postId]; return next; });
        } else {
          setUndoMap(prev => prev[postId] ? { ...prev, [postId]: { ...prev[postId], secondsLeft: seconds } } : prev);
        }
      }, 1000);

      // Store interval id so undo can clear it
      setUndoMap(prev => prev[postId] ? { ...prev, [postId]: { ...prev[postId], intervalId: interval } } : prev);
    } catch (err) {
      console.error('Failed to move post:', err);
      alert(err.response?.data?.detail || 'Failed to move post');
    }
  };

  const handleUndoMove = async (postId) => {
    const info = undoMap[postId];
    if (!info) return;

    // Clear the countdown
    if (info.intervalId) clearInterval(info.intervalId);
    setUndoMap(prev => { const next = { ...prev }; delete next[postId]; return next; });

    try {
      const res = await axios.post(`${API_URL}/api/posts/${postId}/undo-move-to-poopy-butthole`, {
        new_post_id: info.newPostId,
      });

      setPosts(prev =>
        prev.map(p =>
          p.post_id === postId
            ? { ...p, content: res.data.restored_content }
            : p
        )
      );
    } catch (err) {
      console.error('Failed to undo move:', err);
      alert(err.response?.data?.detail || 'Failed to undo move');
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-12 md:py-20 overflow-hidden">
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="bg-card/85 dark:bg-card/80 backdrop-blur-sm rounded-2xl py-10 px-6 shadow-lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex justify-center mb-6">
              <div
                className="w-28 md:w-40 rounded-2xl overflow-hidden shadow-lg shadow-forest-900/30"
                style={{ aspectRatio: `${HERO_IMAGE_WIDTH} / ${HERO_IMAGE_HEIGHT}` }}
              >
                <img
                  src={HERO_IMAGE}
                  alt="Secrets Club"
                  width={HERO_IMAGE_WIDTH}
                  height={HERO_IMAGE_HEIGHT}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 tracking-tight">
              Secrets Club
            </h1>
            <p className="text-xl md:text-2xl text-foreground mb-2">
              Truly Anonymous Confessions and Advice
            </p>
            
            <SiteDescription />
            
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              {(useNativeConfessionFlow || googleFormUrl) && (
                user && user.email_verified ? (
                  useNativeConfessionFlow ? (
                    <Link to="/submit-confession">
                      <Button className="btn-primary text-lg px-8 py-6" data-testid="submit-confession-btn">
                        <Sparkles className="w-5 h-5 mr-2" />
                        Submit a Confession
                      </Button>
                    </Link>
                  ) : (
                    <a 
                      href={googleFormUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <Button className="btn-primary text-lg px-8 py-6" data-testid="submit-confession-btn">
                        <Sparkles className="w-5 h-5 mr-2" />
                        Submit a Confession
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                  )
                ) : user && !user.email_verified ? (
                  <Button 
                    className="btn-primary text-lg px-8 py-6" 
                    data-testid="submit-confession-btn"
                    onClick={() => navigate('/verify-account')}
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    Submit a Confession
                    {!useNativeConfessionFlow && <ExternalLink className="w-4 h-4 ml-2" />}
                  </Button>
                ) : (
                  <Link to="/login">
                    <Button className="btn-primary text-lg px-8 py-6" data-testid="submit-confession-btn">
                      <Sparkles className="w-5 h-5 mr-2" />
                      Submit a Confession
                      {!useNativeConfessionFlow && <ExternalLink className="w-4 h-4 ml-2" />}
                    </Button>
                  </Link>
                )
              )}
              {!user && (
                <Link to="/register">
                  <Button variant="outline" className="text-lg px-8 py-6">
                    Join the Community
                  </Button>
                </Link>
              )}
            </div>
          </motion.div>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="max-w-2xl mx-auto px-4">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="mb-6">
          <TabsList className="w-full h-auto flex-wrap justify-center sm:justify-start rounded-2xl border border-white/10 bg-black/35 p-1.5 text-white/75 backdrop-blur-md shadow-lg">
            <TabsTrigger
              value="main"
              className="min-w-0 basis-[calc(50%-0.375rem)] sm:flex-1 rounded-xl px-3 sm:px-4 py-2.5 text-sm sm:text-base text-white/75 hover:bg-black/20 hover:text-white data-[state=active]:bg-black/60 data-[state=active]:text-white data-[state=active]:shadow-none"
              data-testid="main-tab"
            >
              Confessions
            </TabsTrigger>
            <TabsTrigger
              value="filtered"
              className="min-w-0 basis-[calc(50%-0.375rem)] sm:flex-1 rounded-xl px-3 sm:px-4 py-2.5 text-sm sm:text-base leading-tight text-center whitespace-normal text-white/75 hover:bg-black/20 hover:text-white data-[state=active]:bg-black/60 data-[state=active]:text-white data-[state=active]:shadow-none"
              data-testid="filtered-tab"
              aria-label="Poopy Butthole"
            >
              <span className="flex flex-col items-center">
                <span>Poopy</span>
                <span>Butthole</span>
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="archives"
              className="min-w-0 basis-[calc(50%-0.375rem)] sm:flex-1 rounded-xl px-3 sm:px-4 py-2.5 text-sm sm:text-base text-white/75 hover:bg-black/20 hover:text-white data-[state=active]:bg-black/60 data-[state=active]:text-white data-[state=active]:shadow-none"
              data-testid="archives-tab"
            >
              Archives
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* About Tab Content */}
        {activeTab === 'about' && (
          <Card className="mb-6" data-testid="about-content">
            <CardContent className="py-6">
              <div className="flex items-center gap-2 mb-4">
                <Info className="w-5 h-5 text-forest-600 dark:text-forest-400" />
                <h3 className="font-serif text-xl font-semibold">About Secrets Club</h3>
              </div>
              {aboutText ? (
                <div className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                  {aboutText}
                </div>
              ) : (
                <p className="text-muted-foreground italic">No about information has been added yet.</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Rules Tab Content */}
        {activeTab === 'rules' && (
          <Card className="mb-6" data-testid="rules-content">
            <CardContent className="py-6">
              <div className="flex items-center gap-2 mb-4">
                <ScrollText className="w-5 h-5 text-forest-600 dark:text-forest-400" />
                <h3 className="font-serif text-xl font-semibold">Rules</h3>
              </div>
              {rules.length > 0 ? (
                <ol className="list-decimal list-inside space-y-3">
                  {rules.map((rule, index) => (
                    <li key={rule.rule_id} className="text-foreground leading-relaxed pl-1" data-testid={`rule-item-${index}`}>
                      {rule.text}
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-muted-foreground italic">No rules have been added yet.</p>
              )}
            </CardContent>
          </Card>
        )}
        

        {/* Contributors Tab */}
        {activeTab === 'contributors' && (
          <Card className="mb-6 bg-card/80 backdrop-blur-sm" data-testid="contributors-section">
            <CardContent className="py-6">
              <div className="text-center mb-6">
                <Heart className="w-8 h-8 mx-auto text-primary mb-2" />
                <h2 className="text-2xl font-serif font-bold">Our Contributors</h2>
                <p className="text-muted-foreground text-sm mt-1">Thank you to everyone who helps keep Secrets Club running</p>
              </div>

              {contributorsLoading ? (
                <div className="flex justify-center py-10">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : contributors.tiers.length === 0 ? (
                <div className="text-center py-10 space-y-3">
                  <Shield className="w-12 h-12 mx-auto text-muted-foreground/40" />
                  <p className="text-muted-foreground">No contributors yet.</p>
                  <p className="text-sm text-muted-foreground/70">
                    Be the first to support Secrets Club! Check out our <a href="/membership" className="text-primary hover:underline">membership tiers</a>.
                  </p>
                </div>
              ) : (
                <div className="space-y-8">
                  {contributors.tiers.map((tier) => (
                    <div key={tier.tier_level} data-testid={`contributor-tier-${tier.tier_level}`}>
                      <div className="flex items-center gap-2 mb-4">
                        <Crown className={`w-5 h-5 ${
                          tier.tier_level === 3 ? 'text-red-500' : 
                          tier.tier_level === 2 ? 'text-purple-500' : 
                          'text-blue-500'
                        }`} />
                        <h3 className="font-serif font-semibold text-lg">{tier.tier_name}</h3>
                        <span className="text-xs text-muted-foreground">({tier.members.length} {tier.members.length === 1 ? 'member' : 'members'})</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                        {tier.members.map((member) => (
                          <a
                            key={member.user_id}
                            href={`/profile/${member.user_id}`}
                            className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card/60 hover:bg-card hover:border-primary/30 transition-all group"
                            data-testid={`contributor-${member.user_id}`}
                          >
                            <div className={`w-10 h-10 rounded-full bg-muted flex items-center justify-center overflow-hidden ring-2 ${
                              tier.tier_level === 3 ? 'ring-red-500' : 
                              tier.tier_level === 2 ? 'ring-purple-500' : 
                              'ring-blue-500'
                            }`}>
                              {member.profile_picture ? (
                                <img
                                  src={member.profile_picture}
                                  alt=""
                                  className="w-full h-full object-cover"
                                  style={getAvatarCropStyle({
                                    zoom: member.profile_picture_zoom,
                                    positionX: member.profile_picture_position_x,
                                    positionY: member.profile_picture_position_y,
                                  })}
                                />
                              ) : (
                                <span className="text-sm font-medium text-muted-foreground">
                                  {(member.display_name || '?')[0].toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm truncate group-hover:text-primary transition-colors">{member.display_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {member.tenure_days > 0 
                                  ? `Member for ${member.tenure_days} day${member.tenure_days !== 1 ? 's' : ''}`
                                  : 'New member'}
                              </p>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Poopy Butthole Warning */}
        {activeTab === 'filtered' && (
          <Card className="mb-6 border-yellow-500/50 bg-yellow-50/50 dark:bg-yellow-900/10">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-yellow-800 dark:text-yellow-400">Poopy Butthole Section</h3>
                  <p className="text-sm text-yellow-700 dark:text-yellow-500/80">
                    These confessions were moved out of the main feed because they need extra context, are off-topic,
                    or may not be suitable for the general public feed.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'archives' && (
          <Card className="mb-6 border-sky-500/40 bg-sky-50/50 dark:bg-sky-900/10">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-sky-600 dark:text-sky-400 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-sky-800 dark:text-sky-300">Archives</h3>
                  <p className="text-sm text-sky-700 dark:text-sky-400/90">
                    These are old, outdated secrets from the old Facebook group, but you might still find some intrigue here.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'archives' && !canViewArchives && (
          <Card className={`mb-6 ${archiveUpsellStyles.cardClass}`} data-testid="archives-upsell">
            <CardContent className="py-6">
              <div className="flex items-start gap-3">
                <Lock className={`w-5 h-5 mt-0.5 ${archiveUpsellStyles.iconClass}`} />
                <div>
                  <h3 className={`font-semibold ${archiveUpsellStyles.titleClass}`}>Archives Access</h3>
                  <p className={`text-sm mt-1 ${archiveUpsellStyles.textClass}`}>
                    {archiveUpsellText}
                  </p>
                  <div className="mt-4">
                    <Link to="/membership">
                      <Button className={archiveUpsellStyles.buttonClass} data-testid="archives-upsell-cta">
                        <Crown className="w-4 h-4 mr-2" />
                        View Membership Options
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Topic Filter - only on the main Confessions tab */}
        {activeTab === 'main' && topics.length > 0 && (
          <TopicFilterBar 
            topics={topics}
            selectedTopic={selectedTopic}
            onTopicSelect={handleTopicSelect}
            selectedTag={selectedTag}
            onClearTag={handleClearTag}
          />
        )}

        {(activeTab === 'main' || activeTab === 'filtered' || (activeTab === 'archives' && canViewArchives)) ? (
          <>
            {error && (
              <div className="text-center py-10">
                <p className="text-destructive mb-4">{error}</p>
                <Button onClick={() => window.location.reload()}>Try Again</Button>
              </div>
            )}

            {loading ? (
              <div className="space-y-6">
                {[1, 2, 3].map(i => <PostSkeleton key={i} />)}
              </div>
            ) : posts.length === 0 ? (
              <Card className="text-center py-16">
                <CardContent>
                  <div className="w-20 h-20 mx-auto mb-4 rounded-xl overflow-hidden opacity-40">
                    <ShhIcon className="w-full h-full" />
                  </div>
                  <h3 className="font-serif text-xl font-medium mb-2">
                    {activeTab === 'filtered'
                      ? 'No filtered confessions'
                      : activeTab === 'archives'
                        ? 'No archived confessions'
                        : 'No confessions yet'}
                  </h3>
                  <p className="text-muted-foreground">
                    {activeTab === 'filtered' 
                      ? "The Poopy Butthole section is empty right now."
                      : activeTab === 'archives'
                        ? "The Archives are empty right now."
                      : "Be patient. Secrets will surface soon."
                    }
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6 pb-20" data-testid="posts-feed">
                {posts.map((post, index) => (
                  <React.Fragment key={post.post_id}>
                    <PostCard 
                      post={post} 
                      index={index}
                      onTopicClick={handleTopicSelect}
                      onTagClick={handleTagClick}
                      user={user}
                      roleOrder={roleOrder}
                      onMoveToPoopyButthole={handleMoveToPoopyButthole}
                      undoInfo={undoMap[post.post_id]}
                      onUndoMove={handleUndoMove}
                      onImageClick={handleImageClick}
                      onReact={handlePostReaction}
                      allowedEmojis={allowedEmojis}
                      feedLocation={{ pathname: location.pathname, search: location.search }}
                      onToggleAdEligibility={handleToggleAdEligibility}
                      onBeforeOpenPost={saveFeedMemory}
                      onBackToTop={handleBackToTop}
                    />
                    <AdPlaceholder 
                      user={user} 
                      postIndex={index} 
                      adFrequencyGuest={adFrequencyGuest}
                      adFrequencyMember={adFrequencyMember}
                    />
                  </React.Fragment>
                ))}
              </div>
            )}
          </>
        ) : null}

        {/* Load More Trigger */}
        {(activeTab === 'main' || activeTab === 'filtered' || (activeTab === 'archives' && canViewArchives)) && (
          <div className="space-y-4">
            <div ref={loadMoreRef} className="h-20 flex items-center justify-center">
              {loadingMore && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <div className="w-5 h-5 border-2 border-forest-600 border-t-transparent rounded-full animate-spin" />
                  <span>Loading more...</span>
                </div>
              )}
              {!hasMore && posts.length > 0 && (
                <p className="text-muted-foreground text-sm font-serif italic">That's all the skeletons in the closet...</p>
              )}
            </div>
            {posts.length > 0 && (
              <div className="flex justify-center pb-10">
                <Button variant="outline" onClick={handleBackToTop} data-testid="back-to-top-btn">
                  <ChevronUp className="w-4 h-4 mr-2" />
                  Back To Top
                </Button>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Fullscreen Image Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
          data-testid="image-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full p-2 z-10 transition-colors"
            onClick={() => setLightboxImage(null)}
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightboxImage}
            alt="Full size"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
